import * as React from 'react';

import {
  ISliderConf,
  NativeSlider,
  useBreakpointValue,
} from '@notino/react-styleguide';

import { useReviewFromCache } from '../../hooks';
import { useReviewPhotoModalStore } from '../../store';

import {
  GallerySection,
  NextReviewArrow,
  Slide,
  SlideImage,
  StyledArrow,
} from './styled';
import { useGalleryKeyboardControls } from './useGalleryKeyboardControls';

type Props = {
  showNextReviewArrow?: boolean;
  showPrevReviewArrow?: boolean;
  onNextReviewClick?: () => void;
  onPrevReviewClick?: () => void;
};

export const Gallery = ({
  showNextReviewArrow,
  showPrevReviewArrow,
  onNextReviewClick,
  onPrevReviewClick,
}: Props) => {
  const arrowOffset = useBreakpointValue({ xs: '8px', lg: '24px' });
  const arrowSize = useBreakpointValue({ xs: '1rem', lg: '1.25rem' });
  const reviewId = useReviewPhotoModalStore((x) => x.reviewId);
  const review = useReviewFromCache(reviewId);
  const selectedImage = useReviewPhotoModalStore((x) => x.selectedImage);
  const { setImage } = useReviewPhotoModalStore((x) => x.actions);
  const galleryRef = React.useRef<HTMLDivElement>(null);
  const sliderRef = React.useRef<{
    setCurrentSlide: (slideIndex: number, behavior?: ScrollBehavior) => void;
  }>(null);
  const effectEnabled = React.useRef(true);
  const afterChangeEnabled = React.useRef(false);
  const timeout = React.useRef<NodeJS.Timeout>();

  const images = React.useMemo(() => review?.images || [], [review?.images]);

  useGalleryKeyboardControls({
    sliderRef,
    showNextReviewArrow,
    showPrevReviewArrow,
    onNextReviewClick,
    onPrevReviewClick,
  });

  React.useLayoutEffect(() => {
    afterChangeEnabled.current = false;
  }, [selectedImage, images]);

  React.useEffect(() => {
    if (effectEnabled.current) {
      sliderRef.current?.setCurrentSlide(
        images.findIndex((i) => i.fullSizeUrl === selectedImage.fullSizeUrl),
        'instant' as ScrollBehavior
      );
    }
    const id = setTimeout(() => (afterChangeEnabled.current = true), 10);
    return () => {
      clearTimeout(id);
    };
  }, [selectedImage, images]);

  const afterSlideChange = React.useCallback(
    ({ slideIndex }) => {
      clearTimeout(timeout.current);
      if (!afterChangeEnabled.current) {
        return;
      }
      effectEnabled.current = false;
      timeout.current = setTimeout(() => {
        effectEnabled.current = true;
      }, 10);
      setImage(images[slideIndex]);
    },
    [images, setImage]
  );

  React.useEffect(() => {
    galleryRef.current?.scrollIntoView({
      behavior: 'instant' as ScrollBehavior,
    });
  }, []);

  if (!review || !images.length) {
    return null;
  }

  return (
    <GallerySection ref={galleryRef}>
      {showPrevReviewArrow && (
        <NextReviewArrow
          direction="left"
          color="icon.primary"
          size={arrowSize}
          offset={arrowOffset}
          onClick={onPrevReviewClick}
        />
      )}
      {showNextReviewArrow && (
        <NextReviewArrow
          direction="right"
          color="icon.primary"
          size={arrowSize}
          offset={arrowOffset}
          onClick={onNextReviewClick}
        />
      )}

      <NativeSlider
        slidesToShow={1}
        slidesToScroll={1}
        prevArrow={
          <StyledArrow
            direction="left"
            color="icon.primary"
            size={arrowSize}
            offset={arrowOffset}
          />
        }
        nextArrow={
          <StyledArrow
            direction="right"
            color="icon.primary"
            size={arrowSize}
            offset={arrowOffset}
          />
        }
        afterChange={afterSlideChange}
        sliderRef={sliderRef as ISliderConf['sliderRef']}
      >
        {images.map((image) => (
          <Slide key={image.fullSizeUrl}>
            <SlideImage src={image.fullSizeUrl} />
          </Slide>
        ))}
      </NativeSlider>
    </GallerySection>
  );
};
